import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { supabase } from '../supabaseClient';
import logger from '../utils/logger';
import { FREE_SIGNUP_ENABLED } from '../constants/freeSignup';

// Set base URL for API calls - use environment variable or fallback to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
axios.defaults.baseURL = API_BASE_URL;

  // Session timeout constants (PRODUCTION)
  const SESSION_TIMEOUT_MS = 1 * 60 * 60 * 1000;        // 1 hour for desktop
  const ABSOLUTE_TIMEOUT_MS = 8 * 60 * 60 * 1000;       // 8 hours maximum (quiet logout)
  const WARNING_BEFORE_TIMEOUT_MS = 15 * 60 * 1000;      // 15 minutes warning
  
  // Activity detection thresholds (PRODUCTION)
  const ACTIVITY_THRESHOLD_MS = 5 * 60 * 1000;           // 5 minutes between resets
  const ACTIVITY_DEBOUNCE_MS = 1 * 60 * 1000;            // 1 minute debounce

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [tierInfo, setTierInfo] = useState(null);
  
  // Session management refs
  const sessionTimeoutRef = useRef(null);
  const absoluteTimeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const autoLogoutTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Session management functions
  const clearAllTimers = useCallback(() => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
    if (absoluteTimeoutRef.current) {
      clearTimeout(absoluteTimeoutRef.current);
      absoluteTimeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (autoLogoutTimeoutRef.current) {
      clearTimeout(autoLogoutTimeoutRef.current);
      autoLogoutTimeoutRef.current = null;
    }
  }, []);



  const startSessionTimers = useCallback(() => {
    // Clear ALL timers when starting fresh (user activity or session extension)
    clearAllTimers();
    
    // Calculate warning time (session timeout - warning period)
    const warningTime = SESSION_TIMEOUT_MS - WARNING_BEFORE_TIMEOUT_MS; // 1 hour - 15 min = 45 min
    
    // Set warning timer (45 minutes for desktop)
    sessionTimeoutRef.current = setTimeout(() => {
      setShowSessionWarning(true);
      
      // Auto-logout after warning period - this MUST happen regardless of activity
      autoLogoutTimeoutRef.current = setTimeout(async () => {
        // Force logout by clearing state and redirecting
        setShowSessionWarning(false);
        setToken(null);
        setUser(null);
        setLoading(false);
        delete axios.defaults.headers.common['Authorization'];
        
        // Ensure Supabase logout completes before redirect
        if (supabase) {
          try {
            await supabase.auth.signOut();
          } catch (error) {
            // Silent error handling
          }
        }
        
        // Clear any remaining auth data from storage
        try {
          localStorage.removeItem('supabase.auth.token');
          sessionStorage.clear();
        } catch (error) {
          // Silent error handling
        }
        
        // Force page reload to ensure logout
        window.location.href = '/login';
      }, WARNING_BEFORE_TIMEOUT_MS);
    }, warningTime);
    
    // Set absolute timer (8 hours) - QUIET LOGOUT (no warning)
    absoluteTimeoutRef.current = setTimeout(async () => {
      // Force logout by clearing state and redirecting (no warning dialog)
      setShowSessionWarning(false);
      setToken(null);
      setUser(null);
      setLoading(false);
      delete axios.defaults.headers.common['Authorization'];
      
      // Ensure Supabase logout completes before redirect
      if (supabase) {
        try {
          await supabase.auth.signOut();
        } catch (error) {
          // Silent error handling
        }
      }
      
      // Clear any remaining auth data from storage
      try {
        localStorage.removeItem('supabase.auth.token');
        sessionStorage.clear();
      } catch (error) {
        // Silent error handling
      }
      
      // Force page reload to ensure logout
      window.location.href = '/login';
    }, ABSOLUTE_TIMEOUT_MS);
  }, [clearAllTimers]);

  const resetSessionTimers = useCallback(() => {
    lastActivityRef.current = Date.now();
    startSessionTimers();
  }, [startSessionTimers]);

  const extendSession = useCallback(() => {
    setShowSessionWarning(false);
    // Clear ALL timers including auto-logout when user explicitly extends
    clearAllTimers();
    resetSessionTimers();
  }, [resetSessionTimers, clearAllTimers]);

  // User activity detection with smart debouncing
  const handleUserActivity = useCallback(() => {
    // Don't reset timers if warning is already showing
    if (showSessionWarning) {
      return;
    }
    
    // Ignore activity when browser tab is not visible (background)
    if (document.hidden) {
      return;
    }
    
    if (token && user) {
      const now = Date.now();
      const timeSinceLastReset = now - lastActivityRef.current;
      
      // Reset timers on user activity - but only if enough time has passed to prevent excessive resets
      // This ensures the session stays active while the user is working
      if (timeSinceLastReset > ACTIVITY_THRESHOLD_MS) { // 5 minutes threshold
        lastActivityRef.current = now;
        resetSessionTimers();
      }
    }
  }, [token, user, resetSessionTimers, showSessionWarning]);

  // Set up activity listeners with 1-minute debouncing
  useEffect(() => {
    if (!token || !user) {
      return;
    }

    let debounceTimeout = null;
    
    const debouncedActivityHandler = () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      
      debounceTimeout = setTimeout(() => {
        handleUserActivity();
      }, ACTIVITY_DEBOUNCE_MS); // 1 minute debounce
    };
    
    // Use meaningful user actions only (exclude mousemove and scroll to reduce noise)
    const events = ['mousedown', 'click', 'keypress', 'touchstart'];
    
    events.forEach(event => {
      document.addEventListener(event, debouncedActivityHandler, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, debouncedActivityHandler);
      });
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [token, user, handleUserActivity]);

  const forgotPassword = async (email) => {
    if (!supabase) {
      return { 
        success: false, 
        error: 'Supabase client not available'
      };
    }

    try {
      const baseUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
      const redirectUrl = `${baseUrl}/reset-password`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl
      });

      if (error) {
        throw new Error(error.message);
      }

      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.message || 'Password reset failed'
      };
    }
  };

  const fetchTierInfo = useCallback(async () => {
    if (!token) {
      setTierInfo(null);
      return;
    }

    try {
      const response = await axios.get('/user/tier-info');
      setTierInfo(response.data);
    } catch (error) {
      logger.error('Failed to fetch tier info:', error);
      setTierInfo(null);
    }
  }, [token]);

  const resetPassword = async (newPassword) => {
    if (!supabase) {
      return { 
        success: false, 
        error: 'Supabase client not available'
      };
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        throw new Error(error.message);
      }

      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.message || 'Password update failed'
      };
    }
  };

  const logout = async () => {
    if (!token && !user) {
      return;
    }
    
    // Clear all session timers
    clearAllTimers();
    setShowSessionWarning(false);
    
    // Ensure Supabase logout completes
    if (supabase) {
      try {
        await supabase.auth.signOut();
      } catch (error) {
        // Silent error handling
      }
    }
    
    // Clear any remaining auth data from storage
    try {
      localStorage.removeItem('supabase.auth.token');
      sessionStorage.clear();
    } catch (error) {
      // Silent error handling
    }
    
    setToken(null);
    setUser(null);
    setTierInfo(null);
    setLoading(false);
    delete axios.defaults.headers.common['Authorization'];
  };

  const checkAuthStatus = useCallback(async () => {
    try {
      if (!supabase) {
        setLoading(false);
        return;
      }

      // Check Supabase session
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        setToken(null);
        setUser(null);
        setLoading(false);
        return;
      }

      if (session) {
        // User has valid Supabase session
        setUser(session.user);
        setToken(session.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
        setLoading(false);
        
        // Start session timers for authenticated user
        startSessionTimers();
        
        // Fetch tier info for restored session
        fetchTierInfo();
      } else {
        // No valid session
        setToken(null);
        setUser(null);
        setLoading(false);
      }
    } catch (error) {
      setToken(null);
      setUser(null);
      setLoading(false);
    }
  }, [startSessionTimers, fetchTierInfo]);

  // Set up axios defaults and initial auth check
  useEffect(() => {
    // Give Supabase time to initialize and restore session
    const timer = setTimeout(() => {
      checkAuthStatus();
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [checkAuthStatus]);

  // Listen to Supabase auth state changes
  useEffect(() => {
    if (!supabase) {
      return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          if (session) {
            setUser(session.user);
            setToken(session.access_token);
            setLoading(false);
            axios.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
            
            // Start session timers for newly authenticated user
            startSessionTimers();
            
            // Note: fetchTierInfo will be called by INITIAL_SESSION or checkAuthStatus

            // Mirror consent from Supabase metadata to backend on session events
            try {
              const md = (session.user && session.user.user_metadata) ? session.user.user_metadata : {};
              if (md.termsAccepted || md.privacyAccepted) {
                await axios.post('/user/consent', {
                  termsAccepted: !!md.termsAccepted,
                  termsVersion: md.termsVersion || '2025-01-01',
                  privacyAccepted: !!md.privacyAccepted,
                  privacyVersion: md.privacyVersion || '2025-01-01'
                });
              }
            } catch (e) {
              // Non-blocking
            }
          }
        } else if (event === 'SIGNED_OUT') {
          setToken(null);
          setUser(null);
          setTierInfo(null);
          setLoading(false);
          delete axios.defaults.headers.common['Authorization'];
          
          // Clear all session timers
          clearAllTimers();
          setShowSessionWarning(false);
        } else if (event === 'INITIAL_SESSION') {
          // This event fires when Supabase initializes and restores session
          if (session) {
            setUser(session.user);
            setToken(session.access_token);
            setLoading(false);
            axios.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
            
            // Start session timers for restored session
            startSessionTimers();
            
            // Fetch tier info for restored session
            fetchTierInfo();

            // Mirror consent from Supabase metadata to backend on initial session
            try {
              const md = (session.user && session.user.user_metadata) ? session.user.user_metadata : {};
              if (md.termsAccepted || md.privacyAccepted) {
                await axios.post('/user/consent', {
                  termsAccepted: !!md.termsAccepted,
                  termsVersion: md.termsVersion || '2025-01-01',
                  privacyAccepted: !!md.privacyAccepted,
                  privacyVersion: md.privacyVersion || '2025-01-01'
                });
              }
            } catch (e) {
              // Non-blocking
            }
          } else {
            setLoading(false);
          }
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [startSessionTimers, clearAllTimers, fetchTierInfo]);

  const login = async (email, password) => {
    if (!supabase) {
      return { 
        success: false, 
        error: 'Supabase client not available'
      };
    }

    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (authError) {
        throw new Error(authError.message);
      }

      const { user: userData, session } = authData;
      
      if (session?.access_token) {
        setToken(session.access_token);
        setUser(userData);
        axios.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
        
        // Start session timers for newly logged in user
        startSessionTimers();
        
        // Call backend to trigger user creation/sync
        try {
          await axios.get('/auth/check');
        } catch (backendError) {
          // Silent error handling - user is still logged in
        }
        
        // Mirror consent from Supabase metadata to backend on first authenticated session
        try {
          const md = (userData && userData.user_metadata) ? userData.user_metadata : {};
          // Only post if at least one consent flag is present
          if (md.termsAccepted || md.privacyAccepted) {
            await axios.post('/user/consent', {
              termsAccepted: !!md.termsAccepted,
              termsVersion: md.termsVersion || '2025-01-01',
              privacyAccepted: !!md.privacyAccepted,
              privacyVersion: md.privacyVersion || '2025-01-01'
            });
          }
        } catch (e) {
          // Non-blocking: consent mirroring can retry on next session event
        }
        
        // Note: fetchTierInfo will be called by auth state change handlers
      }
      
      return { success: true };
    } catch (error) {
      let errorMessage = 'Login failed';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.detail?.msg) {
          errorMessage = error.response.data.detail.msg;
        }
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const register = async (firstName, lastName, company, email, password) => {
    if (!FREE_SIGNUP_ENABLED) {
      return {
        success: false,
        error: 'Free account signup is temporarily unavailable. Please request a demo.',
      };
    }

    if (!supabase) {
      return { 
        success: false, 
        error: 'Supabase client not available'
      };
    }

    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            company: company || null,
            termsAccepted: true,
            termsVersion: '2025-01-01',
            privacyAccepted: true,
            privacyVersion: '2025-01-01'
          }
        }
      });

      if (authError) {
        throw new Error(authError.message);
      }

      const { user: userData } = authData;
      setUser(userData);
      
      if (authData.session?.access_token) {
        setToken(authData.session.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${authData.session.access_token}`;
        
        // Start session timers for newly registered user
        startSessionTimers();
      }
      
      return { success: true };
    } catch (error) {
      let errorMessage = 'Registration failed';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail;
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.detail?.msg) {
          errorMessage = error.response.data.detail.msg;
        }
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, [clearAllTimers]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    showSessionWarning,
    tierInfo,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    extendSession,
    resetSessionTimers,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
